import React, { useContext, useState } from 'react';
import { style } from 'typestyle';
import { useTranslation } from 'react-i18next';
import OfficeManagerCard from '../cards/office-manager-card';
import CreateOfficeManagerModal from '../modals/create-office-manager-modal.component';
import { registerOfficeManager } from '../registration/registration.service';
import * as styles from '../registration/registration.styles';
import { deleteOfficeManagerById } from '../user/office-user.service';
import type OfficeManagerUser from '../models/officeManagerUser';
import AppConstants from '../../../core/app.constants';
import ActivityIndicator from '../../../core/components/activity-indicator/activity-indicator.component';
import PrimaryButton from '../../../core/components/buttons/primary-button.component';
import { Center } from '../../../core/components/center/center.component';
import Column from '../../../core/components/column/column.component';
import { CrossAxisAlignment, MainAxisAlignment } from '../../../core/components/flex/flex.component';
import notify from '../../../core/components/notify/notify.component';
import Row from '../../../core/components/row/row.component';
import { IsMobileContext } from '../../../core/context/is-mobile';
import StyleConstants from '../../../core/styles.constants';
import type Office from '../../offices/models/office';
import Grid from '../../../core/components/grid/grid.component';
import { useKeycloak } from '../keycloak/KeycloakProvider';

interface Props {
  readonly allOfficeManagers: OfficeManagerUser[] | undefined;
  readonly offices: Office[] | undefined;
}

const RegisterOfficeManagersGroup = ({ allOfficeManagers, offices }: Props): React.JSX.Element => {
  const isMobile = useContext(IsMobileContext);
  const { t } = useTranslation();
  const keycloak = useKeycloak();
  const role = AppConstants.OFFICEMANAGER_ROLE;
  const [showCreateOfficeManagerModal, setShowCreateOfficeManagerModal] = useState(false);
  const [officeManagers, setOfficeManagers] = useState(allOfficeManagers ?? []);
  const sortedOfficeManagers = officeManagers.sort((a, b) => (a.email.toLowerCase() > b.email.toLowerCase() ? 1 : -1));

  const shouldCreateOfficeManager = async (email: string, officeIds: string[]): Promise<void> => {
    const officeManagerResult = await registerOfficeManager({ email, role, officeIds }, keycloak);

    if (!officeManagerResult || officeManagerResult.users == null) {
      notify(t('office-manager-registration-error-message'), false);
      return;
    }

    const officeManagersCopy = [...officeManagers, ...officeManagerResult.users];

    setOfficeManagers(officeManagersCopy);
    notify(t('user-registration-success-message'), true);
  };

  const shouldDeleteOfficeManager = async (officeManagerToDelete: OfficeManagerUser): Promise<void> => {
    if (officeManagerToDelete.id != null) {
      await deleteOfficeManagerById(officeManagerToDelete.id, officeManagerToDelete.email, keycloak);
      const remaningOfficeManagers = officeManagers.filter((officeManager) => officeManager.id !== officeManagerToDelete.id);
      setOfficeManagers(remaningOfficeManagers);
    } else {
      notify(t('office-manager-delete-error-message'), false);
    }
  };

  const handleShowCreate = (): void => {
    setShowCreateOfficeManagerModal(true);
  };
  const handleClose = (): void => {
    setShowCreateOfficeManagerModal(false);
  };

  return (
    <div>
      {allOfficeManagers == null ? (
        <Center>
          <ActivityIndicator size={20} color={StyleConstants.GREY_COLOR} />
        </Center>
      ) : (
        <div className={style(styles.bodyMargin(isMobile))}>
          <Column crossAxisAlignment={CrossAxisAlignment.Stretch}>
            <div className={style(styles.officeManagerCard(isMobile))}>
              {isMobile ? (
                <Column>
                  <span className={style(styles.columnOfficeManagerHeader(isMobile))}>{t('registered-office-managers').toUpperCase()}</span>
                  <div className={style(styles.officeManagerCreateButton(isMobile))}>
                    <PrimaryButton fullWidth onClick={handleShowCreate}>
                      {t('create-office-manager')}
                    </PrimaryButton>
                  </div>
                </Column>
              ) : (
                <Row mainAxisAlignment={MainAxisAlignment.SpaceBetween} crossAxisAlignment={CrossAxisAlignment.Center}>
                  <span className={style(styles.columnOfficeManagerHeader(isMobile))}>{t('registered-office-managers').toUpperCase()}</span>
                  <div className={style(styles.officeManagerCreateButton(isMobile))}>
                    <PrimaryButton onClick={handleShowCreate}>{t('create-office-manager')}</PrimaryButton>
                  </div>
                </Row>
              )}

              {sortedOfficeManagers.length === 0 ? (
                <span className={style(styles.emptyListMessage)}>{t('no-office-managers-available')}</span>
              ) : (
                <Grid
                  rowClass={style(styles.gridRow)}
                  numberOfColumns={isMobile ? 1 : 3}
                  itemSpacing={12}
                  numberOfItems={sortedOfficeManagers.length}
                  renderItem={(index: number): React.JSX.Element => {
                    const officeManager = sortedOfficeManagers[index];
                    return (
                      officeManager && (
                        <div className={isMobile ? style(styles.rowItem(true)) : undefined} key={officeManager.id}>
                          <OfficeManagerCard
                            user={officeManager}
                            handleDeleteOfficeManager={(manager): void => {
                              shouldDeleteOfficeManager(manager);
                            }}
                          />
                        </div>
                      )
                    );
                  }}
                />
              )}
            </div>
          </Column>
          <CreateOfficeManagerModal
            showModal={showCreateOfficeManagerModal}
            handleClose={handleClose}
            handleCreate={(email: string, officesIds: string[]): void => {
              shouldCreateOfficeManager(email, officesIds);
            }}
            offices={offices}
          />
        </div>
      )}
    </div>
  );
};

export default RegisterOfficeManagersGroup;
